import { useCallback, useRef, useState } from "react";
import { ACTION_ERROR_CODES, type CardState, type ConfirmRequest, type ConfirmResponse, type ResultCard } from "./contract";
import { SLOT_FIELD } from "./constants";
import { describeFailure, toActionFailure, type FailureView } from "./errors";
import { toArguments, type FormValues } from "./form-schema";
import type { ActionLabels } from "./labels";

/** `POST /public/v1/actions/{run_id}/confirm` bound to one run by the host. */
export type ConfirmHandler = (request: ConfirmRequest) => Promise<ConfirmResponse>;

export interface SubmitInput {
  values: FormValues;
  slotId?: string;
  /** Bot-check token (web app only), fresh per attempt. */
  captchaToken?: string | null;
}

export interface CardFailure extends FailureView {
  code: string;
  /** Fields the server rejected (`validation_error`). */
  fields: string[];
}

export interface ConfirmFlowOptions {
  confirm: ConfirmHandler | undefined;
  labels: ActionLabels;
  timeZone: string;
  /** Result shown when the server answers without a `result` card. */
  fallbackResult: (response: ConfirmResponse) => ResultCard;
  /** The card turned into its result (`succeeded`) or can no longer be used (`expired`). */
  onStateChange?: ((state: CardState, result?: ResultCard) => void) | undefined;
  /** `slot_unavailable` (the picked time was just taken) or a `validation_error` on the time itself. */
  onSlotUnavailable?: (() => void) | undefined;
}

export interface ConfirmFlow {
  submitting: boolean;
  failure: CardFailure | null;
  result: ResultCard | null;
  expired: boolean;
  /** Finished attempts; hosts remount a single-use bot check when it changes. */
  attempts: number;
  submit: (input: SubmitInput) => Promise<void>;
  /** Sends the last attempt again (same idempotency key); `captchaToken` replaces the spent one. */
  retry: (fresh?: Pick<SubmitInput, "captchaToken">) => Promise<void>;
}

const HEX_RADIX = 16;
const RANDOM_BYTES = 16;

/** One key per card; reused for every retry so a lost response never books twice. */
export function newIdempotencyKey(): string {
  const cryptoApi = globalThis.crypto as Crypto | undefined;
  if (typeof cryptoApi?.randomUUID === "function") return cryptoApi.randomUUID();
  const bytes = new Uint8Array(RANDOM_BYTES);
  if (typeof cryptoApi?.getRandomValues === "function") cryptoApi.getRandomValues(bytes);
  else for (let index = 0; index < bytes.length; index += 1) bytes[index] = Math.floor(Math.random() * 256);
  return Array.from(bytes, (byte) => byte.toString(HEX_RADIX).padStart(2, "0")).join("");
}

function buildRequest(input: SubmitInput, key: string, timeZone: string): ConfirmRequest {
  const request: ConfirmRequest = { arguments: toArguments(input.values), consent: true, idempotency_key: key, timezone: timeZone };
  if (input.slotId) request.slot_id = input.slotId;
  if (input.captchaToken) request.turnstile_token = input.captchaToken;
  return request;
}

/** Submit / error / retry state shared by the booking and lead cards. */
export function useConfirmFlow({ confirm, labels, timeZone, fallbackResult, onStateChange, onSlotUnavailable }: ConfirmFlowOptions): ConfirmFlow {
  const key = useRef<string | null>(null);
  const last = useRef<SubmitInput | null>(null);
  const inFlight = useRef(false);
  const [submitting, setSubmitting] = useState(false);
  const [failure, setFailure] = useState<CardFailure | null>(null);
  const [result, setResult] = useState<ResultCard | null>(null);
  const [expired, setExpired] = useState(false);
  const [attempts, setAttempts] = useState(0);

  const fail = useCallback(
    (error: unknown) => {
      const info = toActionFailure(error);
      if (info.code === ACTION_ERROR_CODES.expired) {
        setExpired(true);
        onStateChange?.("expired");
        return;
      }
      if (info.code === ACTION_ERROR_CODES.slotUnavailable || info.fields.includes(SLOT_FIELD)) onSlotUnavailable?.();
      setFailure({ ...describeFailure(info, labels), code: info.code, fields: info.fields });
    },
    [labels, onSlotUnavailable, onStateChange],
  );

  const submit = useCallback(
    async (input: SubmitInput) => {
      if (inFlight.current) return;
      inFlight.current = true;
      last.current = input;
      key.current ??= newIdempotencyKey();
      setSubmitting(true);
      setFailure(null);
      try {
        if (!confirm) throw new Error("no confirm handler");
        const response = await confirm(buildRequest(input, key.current, timeZone));
        const card = response.result ?? fallbackResult(response);
        setResult(card);
        onStateChange?.("succeeded", card);
      } catch (error) {
        fail(error);
      } finally {
        inFlight.current = false;
        setSubmitting(false);
        setAttempts((count) => count + 1);
      }
    },
    [confirm, fail, fallbackResult, onStateChange, timeZone],
  );

  const retry = useCallback(
    async (fresh?: Pick<SubmitInput, "captchaToken">) => {
      if (last.current) await submit({ ...last.current, ...fresh });
    },
    [submit],
  );

  return { submitting, failure, result, expired, attempts, submit, retry };
}
